/**
 * Persist customer debt collection (Créance-Einzug) + optional cash register cash_in.
 */

import { queueBusinessPayloadWrite } from '../../../infrastructure/storage/businessPayloadWrite';
import { publishDomainEvent } from '../../../infrastructure/events/domainEventBus';
import { DOMAIN_EVENTS } from '../../../infrastructure/events/domainEventTypes';
import {
  planCustomerDebtPayment,
  applyCustomerDebtPayment
} from './debtCollection';

/**
 * @param {object} params
 * @param {string} params.businessId
 * @param {string|number} params.customerId
 * @param {string} [params.customerName]
 * @param {number|string} params.amount
 * @param {string} [params.paymentMethod]
 * @param {string|null} [params.electronicPaymentMethod]
 * @param {string} [params.note]
 * @param {string|null} [params.workerId]
 * @param {string|null} [params.workerName]
 * @param {string|null} [params.actorRole]
 */
export async function recordCustomerDebtPayment(params = {}) {
  const {
    businessId,
    customerId,
    customerName = '',
    amount,
    paymentMethod = 'cash',
    electronicPaymentMethod = null,
    note = '',
    workerId = null,
    workerName = null,
    actorRole = null
  } = params;

  if (!businessId) {
    return { success: false, error: 'business_id_missing' };
  }
  if (customerId == null || customerId === '') {
    return { success: false, error: 'customer_id_missing' };
  }

  let payment = null;
  let balanceChange = null;
  let finalDebts = [];

  try {
    await queueBusinessPayloadWrite(
      businessId,
      (parsed) => {
        const plan = planCustomerDebtPayment({
          customerId,
          customerName,
          amount,
          paymentMethod,
          electronicPaymentMethod,
          customerDebts: parsed.customerDebts,
          note,
          workerId,
          workerName,
          actorRole
        });
        if (!plan.success) {
          throw Object.assign(new Error(plan.error || 'plan_failed'), {
            code: plan.error || 'plan_failed'
          });
        }
        const applied = applyCustomerDebtPayment(parsed, plan);
        if (!applied?.applied) {
          throw Object.assign(new Error('apply_failed'), { code: 'apply_failed' });
        }
        payment = applied.payment;
        balanceChange = {
          customerId: applied.customerId,
          previousBalance: applied.previousRemaining,
          nextBalance: applied.nextRemaining,
          delta: plan.debtDelta,
          paymentId: payment?.id || null
        };
        finalDebts = parsed.customerDebts;
        return parsed;
      },
      {
        allowInSafeMode: true,
        authoritativeFields: ['customerDebts']
      }
    );
  } catch (err) {
    return {
      success: false,
      error: err?.code || err?.message || 'debt_payment_failed',
      message: err?.message || String(err)
    };
  }

  // Bar-Einzug in aktive Kassensitzung (Journal cash_in → Attendu + Istkasse)
  let cashRegister = { success: true, skipped: true, reason: 'not_cash' };
  if (payment && String(payment.paymentMethod || '').toLowerCase() === 'cash') {
    try {
      const { getCashRegisterRepository } = await import('../../../app/di/container');
      const cashRegisterRepo = getCashRegisterRepository();
      const active = cashRegisterRepo.getActiveSession?.(businessId);
      if (!active) {
        cashRegister = { success: true, skipped: true, reason: 'no_active_session' };
      } else {
        const addResult = cashRegisterRepo.addTransaction(
          {
            type: 'cash_in',
            amount: Number(payment.amount) || 0,
            referenceId: payment.id,
            reason: 'customer_debt_payment',
            customerId: payment.customerId,
            workerId: workerId || active.workerId || null,
            workerName: workerName || active.workerName || null,
            actorRole: actorRole || null
          },
          businessId
        );
        cashRegister = {
          success: Boolean(addResult?.success),
          skipped: false,
          transaction: addResult?.transaction || null,
          error: addResult?.error || null
        };
      }
    } catch (cashErr) {
      cashRegister = {
        success: false,
        skipped: false,
        error: cashErr?.message || String(cashErr)
      };
    }
  }

  publishDomainEvent(DOMAIN_EVENTS.CUSTOMER_BALANCE_CHANGED, {
    businessId,
    ...balanceChange,
    payment,
    source: 'customer_debt_payment'
  });

  return {
    success: true,
    payment,
    balanceChange,
    customerDebts: finalDebts,
    cashRegister
  };
}
